import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useStore } from "vuex";
  import InteractiveMasterplan from "@/v2/components/InteractiveMasterplan.vue";
  import CustomerNavbar from "@/v2/components/CustomerNavbar.vue";
  import { UserRoles } from "@/enums/UserRoles";
  import projectPageApi, {
    ProjectPageData,
  } from "@/services/api/projectPageApi";
  import { InteractiveMasterplanInterface } from "@/types/InteractiveMasterplanInterface";
  import CustomerBottomNavigation from "@/v2/components/CustomerBottomNavigation.vue";

  type BottomNavView =
    | "hero"
    | "browse"
    | "detail"
    | "eoi"
    | "eoi-success"
    | "map"
    | "resources";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'MasterplanView',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const isEmbedded = computed(() => route.query.embed === "true");
  const estateId = computed(() => route.params.estateId as string);
  const matchingHouseDesignId = computed(
    () => route.query.houseDesignId as string | undefined
  );
  const matchingFacadeId = computed(
    () => route.query.facadeId as string | undefined
  );
  const loading = ref(true);
  const error = ref("");
  const hasMasterplan = ref(false);
  const isLotDetailModalOpen = ref(false);
  const masterplanData = ref<InteractiveMasterplanInterface | null>(null);
  const projectPageData = ref<ProjectPageData | null>(null);
  const isLandSubdivision = computed(() =>
    Boolean(projectPageData.value?.landSubContent)
  );
  const estateName = ref("");
  const isMobileMapExpanded = ref(false);

  const overlayVisible = ref(true);
  const loadingPercent = ref(0);
  const loadingDone = ref(false);
  let counterFrameId: number | null = null;
  let counterStartTime = 0;

  const slugifyEstateTitle = (title?: string) => {
    if (!title) return "";

    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const estateSlug = computed(() => slugifyEstateTitle(estateName.value));

  const startCounter = () => {
    const DURATION = 8000;
    const K = 2.5;

    counterStartTime = performance.now();

    const tick = (now: number) => {
      if (loadingDone.value) {
        loadingPercent.value = 100;
        setTimeout(() => {
          overlayVisible.value = false;
        }, 250);
        return;
      }

      const elapsed = now - counterStartTime;
      const t = elapsed / DURATION;
      const target = Math.floor(90 * (1 - Math.exp(-K * t)));

      if (target > loadingPercent.value) {
        loadingPercent.value = target;
      }

      counterFrameId = requestAnimationFrame(tick);
    };

    counterFrameId = requestAnimationFrame(tick);
  };

  const onMapReady = () => {
    loadingDone.value = true;
  };

  watch([loading, error, hasMasterplan], () => {
    if (!loading.value && (error.value || !hasMasterplan.value)) {
      loadingDone.value = true;
    }
  });

  async function fetchProjectPageData() {
    try {
      const { data } = await projectPageApi.getByEstateIdPublic(estateId.value);

      projectPageData.value = data;
    } catch (err) {
      console.log("No project page data found:", err);
    }
  }

  function goBack() {
    const estate = store.state.user.landownerEstates?.find(
      (e: any) => e._id === estateId.value
    );

    if (estate?.title) {
      const slug = estate.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

      router.push(
        isLandSubdivision.value
          ? `/p/${slug}`
          : `/residential-communities/${slug}`
      );
    } else {
      router.back();
    }
  }

  function handleBottomNavigate(view: BottomNavView) {
    if (view === "browse") {
      if (isLandSubdivision.value) return;

      router.push({
        name: "BrowseHomeDesignsV2",
        params: { estateId: estateId.value },
        query: isEmbedded.value ? { embed: "true" } : {},
      });
      return;
    }

    if (view === "resources" && estateSlug.value) {
      router.push({
        name: isLandSubdivision.value
          ? "PublicProjectPageCanonical"
          : "PublicProjectPage",
        params: { slug: estateSlug.value },
      });
    }
  }

  onMounted(async () => {
    startCounter();

    try {
      const estate = store.state.user.landownerEstates?.find(
        (e: any) => e._id === estateId.value
      );

      if (estate?.title) {
        estateName.value = estate.title;
      }

      const [, masterplanRes] = await Promise.all([
        fetchProjectPageData(),
        store.dispatch(
          "interactiveMasterplan/getMasterplanByEstateId",
          estateId.value
        ),
      ]);

      const data = masterplanRes.data;

      masterplanData.value = data;
      hasMasterplan.value = !!data;

      if (!estateName.value) {
        const populatedEstate = data?.estateId;

        if (
          populatedEstate &&
          typeof populatedEstate === "object" &&
          populatedEstate.title
        ) {
          estateName.value = populatedEstate.title;
        } else if (projectPageData.value?.residentialContent?.brandText) {
          estateName.value = projectPageData.value.residentialContent.brandText;
        }
      }
    } catch (err: any) {
      console.error("Failed to load masterplan:", err);

      if (err.response?.status === 404) {
        hasMasterplan.value = false;
      } else {
        error.value = "Failed to load masterplan. Please try again.";
      }
    } finally {
      loading.value = false;
    }
  });

  onUnmounted(() => {
    if (counterFrameId !== null) {
      cancelAnimationFrame(counterFrameId);
    }
  });

const __returned__ = { route, router, store, isEmbedded, estateId, matchingHouseDesignId, matchingFacadeId, loading, error, hasMasterplan, isLotDetailModalOpen, masterplanData, projectPageData, isLandSubdivision, estateName, isMobileMapExpanded, overlayVisible, loadingPercent, loadingDone, get counterFrameId() { return counterFrameId }, set counterFrameId(v) { counterFrameId = v }, get counterStartTime() { return counterStartTime }, set counterStartTime(v) { counterStartTime = v }, slugifyEstateTitle, estateSlug, startCounter, onMapReady, fetchProjectPageData, goBack, handleBottomNavigate, ref, computed, onMounted, onUnmounted, watch, get useRoute() { return useRoute }, get useRouter() { return useRouter }, get useStore() { return useStore }, InteractiveMasterplan, CustomerNavbar, get UserRoles() { return UserRoles }, get projectPageApi() { return projectPageApi }, get ProjectPageData() { return ProjectPageData }, get InteractiveMasterplanInterface() { return InteractiveMasterplanInterface }, CustomerBottomNavigation }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})